import { useEffect, useMemo, useState } from "react";
import { X, CheckCircle2, Trophy, Sparkles } from "lucide-react";

/**
 * SponsorRivalryPoll → Home micro-poll deck.
 *
 * Shows ONE compact card at a time (brand rivalry OR live match prediction).
 * Each card has × to dismiss and Yes/No or A/B answers. Answered or dismissed
 * cards never reappear for the same match. Brand prefs are global; predictions
 * are match-scoped. No bottom-fixed overlays — renders inline in Home.
 */

const BRAND_DONE_KEY = "vs:sponsor_rivalry_done_v2"; // ids done globally
const PRED_DONE_PREFIX = "vs:match_pred_done_v2:"; // + matchId

type Rivalry = {
  id: string;
  category: string;
  a: string;
  b: string;
  sponsor?: "a" | "b"; // which side is the sponsored brand
};
const RIVALRIES: Rivalry[] = [
  { id: "cola", category: "Bebida", a: "Coca-Cola", b: "Pepsi", sponsor: "a" },
  { id: "beer", category: "Cerveza", a: "Budweiser", b: "Corona", sponsor: "a" },
  { id: "burger", category: "Comida rápida", a: "McDonald's", b: "Burger King", sponsor: "a" },
  { id: "snack", category: "Snack", a: "Lays", b: "Doritos" },
  { id: "sport", category: "Deportiva", a: "Adidas", b: "Nike", sponsor: "a" },
];

type EventPredict = { id: string; label: string; emoji: string };
const EVENTS: EventPredict[] = [
  { id: "gol10", label: "¿Habrá gol en los próximos 10 minutos?", emoji: "⚽" },
  { id: "amarilla", label: "¿Habrá tarjeta amarilla?", emoji: "🟨" },
  { id: "roja", label: "¿Habrá tarjeta roja?", emoji: "🟥" },
  { id: "penalti", label: "¿Habrá penalti?", emoji: "🎯" },
  { id: "var", label: "¿Habrá revisión del VAR?", emoji: "📺" },
  { id: "proxGol", label: "¿Quién marcará el próximo gol? (Local / Visitante)", emoji: "🎯" },
  { id: "remontada", label: "¿Habrá remontada en el partido?", emoji: "🔄" },
  { id: "primero", label: "¿Quién anota primero? (Local / Visitante)", emoji: "🥇" },
  { id: "empate", label: "¿Termina en empate?", emoji: "🤝" },
  { id: "antesDescanso", label: "¿Habrá gol antes del descanso?", emoji: "⏱️" },
];

function readSet(key: string): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(key);
    return new Set<string>(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}
function writeSet(key: string, set: Set<string>) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(Array.from(set)));
  } catch {
    /* ignore */
  }
}

type Props = { matchId?: string | null };

export function SponsorRivalryPoll({ matchId }: Props = {}) {
  const predKey = PRED_DONE_PREFIX + (matchId || "global");
  const [brandDone, setBrandDone] = useState<Set<string>>(() => readSet(BRAND_DONE_KEY));
  const [predDone, setPredDone] = useState<Set<string>>(() => readSet(predKey));

  useEffect(() => { setPredDone(readSet(predKey)); }, [predKey]);
  useEffect(() => { writeSet(BRAND_DONE_KEY, brandDone); }, [brandDone]);
  useEffect(() => { writeSet(predKey, predDone); }, [predDone, predKey]);

  // Pick next open item: alternate brand → prediction → brand …
  const queue = useMemo(() => {
    const brands = RIVALRIES.filter((r) => !brandDone.has(r.id));
    const preds = EVENTS.filter((e) => !predDone.has(e.id));
    const out: Array<{ kind: "brand"; item: Rivalry } | { kind: "pred"; item: EventPredict }> = [];
    const max = Math.max(brands.length, preds.length);
    for (let i = 0; i < max; i++) {
      if (brands[i]) out.push({ kind: "brand", item: brands[i] });
      if (preds[i]) out.push({ kind: "pred", item: preds[i] });
    }
    return out;
  }, [brandDone, predDone]);

  // Rotación automática: avanza el índice cada 12s para que las encuestas
  // no queden estáticas durante el partido. Se reinicia al cambiar la cola.
  const [rotIdx, setRotIdx] = useState(0);
  useEffect(() => { setRotIdx(0); }, [queue.length]);
  useEffect(() => {
    if (queue.length <= 1) return;
    const id = window.setInterval(() => {
      setRotIdx((i) => (i + 1) % queue.length);
    }, 12_000);
    return () => window.clearInterval(id);
  }, [queue.length]);
  const current = queue[rotIdx] ?? queue[0];
  const [justAnswered, setJustAnswered] = useState<string | null>(null);

  if (!current) return null;

  const dismissBrand = (id: string) => setBrandDone((s) => new Set(s).add(id));
  const dismissPred = (id: string) => setPredDone((s) => new Set(s).add(id));

  if (current.kind === "brand") {
    const r = current.item;
    const answered = justAnswered === r.id;
    return (
      <div data-testid="sponsor-rivalry-card" className="mb-3 mx-auto max-w-md rounded-2xl border border-primary/40 bg-gradient-to-br from-background via-background to-primary/5 p-3 shadow-lg">
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-primary">
              <Trophy className="h-3 w-3" /> ¿Qué prefieres? · {r.category}
            </div>
            <div className="text-sm font-bold leading-tight">Elige tu marca favorita</div>
          </div>
          <button
            type="button"
            onClick={() => dismissBrand(r.id)}
            aria-label="Cerrar encuesta"
            className="rounded-full p-1 text-muted-foreground hover:bg-accent/40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-stretch gap-2">
          {(["a", "b"] as const).map((side) => {
            const name = side === "a" ? r.a : r.b;
            const isSponsor = r.sponsor === side;
            return (
              <button
                key={side}
                type="button"
                onClick={() => {
                  setJustAnswered(r.id);
                  setTimeout(() => dismissBrand(r.id), 700);
                }}
                className="relative flex-1 rounded-xl border border-border/60 bg-card/40 px-3 py-2.5 text-sm font-bold transition hover:border-primary/60 hover:bg-accent/30 active:scale-[0.98]"
              >
                {isSponsor && (
                  <span className="absolute -top-1.5 right-2 rounded-full bg-amber-500 px-1.5 py-[1px] text-[8px] font-black uppercase tracking-wider text-black">
                    Patrocinado
                  </span>
                )}
                {name}
              </button>
            );
          })}
        </div>
        {answered && (
          <div className="mt-2 flex items-center gap-1.5 text-[10px] font-bold text-emerald-500">
            <CheckCircle2 className="h-3 w-3" /> ¡Gracias!
          </div>
        )}
      </div>
    );
  }

  const e = current.item;
  const answered = justAnswered === e.id;
  return (
    <div data-testid="sponsor-rivalry-card" className="mb-3 mx-auto max-w-md rounded-2xl border border-amber-500/40 bg-gradient-to-br from-background via-background to-amber-500/5 p-3 shadow-lg">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-amber-500">
            <Sparkles className="h-3 w-3" /> Reto del partido
          </div>
          <div className="text-sm font-bold leading-tight">{e.emoji} {e.label}</div>
        </div>
        <button
          type="button"
          onClick={() => dismissPred(e.id)}
          aria-label="Cerrar reto"
          className="rounded-full p-1 text-muted-foreground hover:bg-accent/40"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { setJustAnswered(e.id); setTimeout(() => dismissPred(e.id), 700); }}
          className="flex-1 rounded-xl bg-emerald-500/15 px-3 py-2 text-sm font-black uppercase text-emerald-500 transition hover:bg-emerald-500/25 active:scale-[0.98]"
        >Sí</button>
        <button
          type="button"
          onClick={() => { setJustAnswered(e.id); setTimeout(() => dismissPred(e.id), 700); }}
          className="flex-1 rounded-xl bg-rose-500/15 px-3 py-2 text-sm font-black uppercase text-rose-500 transition hover:bg-rose-500/25 active:scale-[0.98]"
        >No</button>
      </div>
      {answered && (
        <div className="mt-2 flex items-center gap-1.5 text-[10px] font-bold text-emerald-500">
          <CheckCircle2 className="h-3 w-3" /> Predicción registrada · suma puntos si aciertas
        </div>
      )}
    </div>
  );
}
